import { customElement, property, state } from "lit/decorators.js";
import { html, LitElement, TemplateResult } from "lit";

@customElement("floating-select-menu")
export class FloatingSelectMenu extends LitElement {
  @property({ type: Array })
  items = []; // Can contain strings, objects with 'color', 'label', 'url', 'image', or 'sprite'

  @property({ type: String })
  menuPosition: "top" | "bottom" | "left" | "right" = "bottom";

  @property({ type: Object })
  defaultItem: string | { label: string; color?: string } | null = null;

  @property({ type: String })
  placeholder = ""; // Optional placeholder

  @state()
  public isOpen = false;

  @state()
  private selectedItem: any = null;

  protected createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("click", this.handleInternalClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("click", this.handleInternalClick);
    document.removeEventListener("click", this.closeMenuOnClickOutside);
  }

  private handleInternalClick(event: Event) {
    event.stopPropagation();
  }

  openMenu() {
    this.isOpen = true;
    document.addEventListener("click", this.closeMenuOnClickOutside);
  }

  closeMenu() {
    this.isOpen = false;
    document.removeEventListener("click", this.closeMenuOnClickOutside);
  }

  closeMenuOnClickOutside = (event: MouseEvent) => {
    if (!this.shadowRoot?.contains(event.target as Node)) {
      this.closeMenu();
    }
  };

  selectItem(
    item:
      | string
      | {
          label: string;
          color?: string | undefined;
          image?: string | undefined;
          sprite?: string | undefined;
          row?: number | undefined;
          col?: number | undefined;
        }
  ) {
    this.selectedItem = item;

    this.dispatchEvent(
      new CustomEvent("item-selected", {
        detail: { item },
        bubbles: true,
        composed: true,
      })
    );
    this.closeMenu();
  }

  render() {
    // Log the defaultItem and selectedItem for debugging

    // Determine the display text
    let displayText: string | TemplateResult = "";
    if (this.selectedItem) {
      displayText =
        typeof this.selectedItem === "string"
          ? this.selectedItem
          : this.selectedItem.sprite
          ? html`
              <div style="display: flex; gap: 10px; align-items: center;">
                <div
                  class="sprite"
                  style="
                background-image: url('${this.selectedItem.sprite}');
                background-position: ${this.selectedItem.col * -24}px ${this
                    .selectedItem.row * -24}px;
                width: 24px;
                height: 24px;
              "
                ></div>
                ${this.selectedItem.label}
              </div>
            `
          : html`
              <span
                style="display: inline-block; width: 16px; height: 16px; background: ${this
                  .selectedItem.color};"
              ></span>
              ${this.selectedItem.label}
            `;
    } else {
      // Use the placeholder if provided, otherwise use the defaultItem, otherwise fall back to "Select Item"
      displayText =
        this.placeholder ||
        (this.defaultItem
          ? typeof this.defaultItem === "string"
            ? this.defaultItem
            : html`
                <span
                  style="display: inline-block; width: 17px; height: 17px; background: ${this
                    .defaultItem.color}; border-radius: 20%;"
                ></span>
                ${this.defaultItem.label}
              `
          : "Select Item");
    }

    return html`
      <div class="floating-menu-wrapper">
        <button class="btn" @click="${this.openMenu}">${displayText}</button>

        <div
          class="floating-menu${this.isOpen ? " is-open" : ""} ${this
            .menuPosition}"
        >
          ${this.items.map((item: any) =>
            typeof item === "string"
              ? html`
                  <div
                    class="floating-menu-item ${this.selectedItem === item
                      ? "selected"
                      : ""}"
                    @click="${() => this.selectItem(item)}"
                  >
                    ${item}
                  </div>
                `
              : item.url
              ? html`
                  <a
                    href="${item.url}"
                    target="_blank"
                    class="floating-menu-item ${this.selectedItem === item
                      ? "selected"
                      : ""}"
                    @click="${(e) => {
                      this.selectItem(item);
                      e.preventDefault(); // Prevent the link from navigating directly
                      window.open(item.url, "_blank"); // Open the link manually
                    }}"
                  >
                    ${item.image
                      ? html`<img
                          src="${item.image}"
                          alt="${item.label}"
                          class="item-image"
                        />`
                      : html``}
                    <div
                      class="color-box"
                      style="background: ${item.color};"
                    ></div>
                    ${item.label}
                  </a>
                `
              : html`
                  <div
                    class="floating-menu-item ${this.selectedItem === item
                      ? "selected"
                      : ""}"
                    @click="${() => this.selectItem(item)}"
                  >
                    ${item.sprite
                      ? html`<div
                          class="sprite"
                          style="
                            background-image: url('${item.sprite}');
                            background-position: ${item.col *
                          -24}px ${item.row * -24}px;
                            width: 24px;
                            height: 24px;
                          "
                        ></div>`
                      : html``}
                    ${item.image
                      ? html`<img
                          src="${item.image}"
                          alt="${item.label}"
                          class="item-image"
                        />`
                      : html``}
                    <div
                      class="color-box"
                      style="background: ${item.color};"
                    ></div>
                    ${item.label}
                  </div>
                `
          )}
        </div>
      </div>
    `;
  }
}
